import createPalette from '@mui/material/styles/createPalette.js';
import {getContrastRatio} from '@mui/material';

import {
  ACCENT,
  BLUE,
  GREEN,
  GREY,
  INDIGO,
  LIGHT_PRIMARY_TEXT,
  ORANGE,
  PINK,
  PURPLE,
  RED,
  TEAL,
  YELLOW,
} from './Colors.js';
import {THEME_VARIANTS} from './Constants.js';

const colorsToAugment = {
  blue: BLUE,
  green: GREEN,
  indigo: INDIGO,
  orange: ORANGE,
  purple: PURPLE,
  red: RED,
  yellow: YELLOW,
};

/**
 * Adapted from https://github.com/mui/material-ui/blob/master/packages/mui-material/src/styles/createPalette.js#L197
 * to be able to use our own text colors
 */
const getContrastText = (background, contrastThreshold = 3) => {
  const contrastText =
    getContrastRatio(background, GREY[50]) >= contrastThreshold
      ? GREY[50]
      : LIGHT_PRIMARY_TEXT;

  if (process.env.NODE_ENV !== 'production') {
    const contrast = getContrastRatio(background, contrastText);
    if (contrast < 3) {
      // eslint-disable-next-line no-console
      console.error(
        [
          `MUI: The contrast ratio of ${contrast}:1 for ${contrastText} on ${background}`,
          'falls below the WCAG recommended absolute minimum contrast ratio of 3:1.',
          'https://www.w3.org/TR/2008/REC-WCAG20-20081211/#visual-audio-contrast-contrast',
        ].join('\n'),
      );
    }
  }

  return contrastText;
};

/**
 * Go from HUE + SHADE to named colors
 * "light" and "dark" refer to the theme variant not whether the color is "lighter" or "darker"
 * This works by using symmetry but may need revision if we expand our colors
 */
const getNamedColors = (themeMode = 'light') => {
  return Object.keys(colorsToAugment).reduce((acc, colorKey) => {
    const color = colorsToAugment[colorKey];

    let namedColor = {};
    if (themeMode === THEME_VARIANTS.DARK) {
      namedColor = {
        main: color[400],
        light: color[800],
        dark: color[200],
      };
    } else {
      namedColor = {
        main: color[500],
        light: color[50],
        dark: color[700],
      };
    }

    acc[colorKey] = {
      ...colorsToAugment[colorKey],
      ...color,
      ...namedColor,
      contrastText: getContrastText(namedColor.main),
    };

    return acc;
  }, {});
};

// Common Palette
export const BASE = {
  accent: {
    ...ACCENT,
    main: ACCENT[500],
  },
  grey: {
    ...GREY,
    main: GREY[900],
    dark: GREY[100],
  },
  secondary: {
    light: PINK[100],
    main: PINK[500],
    dark: PINK[800],
  },
  teal: TEAL,
  pink: PINK,
  contrastThreshold: 3,
  getContrastText,
};

// Dark Theme Variant
// https://planet.invisionapp.com/d/main/#/console/18949169/404930260/preview

const darkNamedColors = getNamedColors(THEME_VARIANTS.DARK);
const defaultDarkPalette = createPalette({mode: THEME_VARIANTS.DARK});

export const DARK = {
  ...defaultDarkPalette,
  ...BASE,
  background: {
    paper: GREY[900],
  },
  divider: GREY[600],
  primary: {
    lighter: TEAL[50],
    light: TEAL[100],
    main: TEAL[500],
    dark: TEAL[900],
  },
  text: {
    primary: GREY[50],
    secondary: GREY[300],
  },
  mode: THEME_VARIANTS.DARK,
  ...darkNamedColors,
  success: {
    light: darkNamedColors.green.light,
    main: darkNamedColors.green.main,
    dark: darkNamedColors.green.dark,
  },
  error: {
    light: darkNamedColors.red.light,
    main: darkNamedColors.red.main,
    dark: darkNamedColors.red.dark,
  },
  info: {
    light: darkNamedColors.blue.light,
    main: darkNamedColors.blue.main,
    dark: darkNamedColors.blue.dark,
  },
  warning: {
    light: darkNamedColors.orange.light,
    main: darkNamedColors.orange.main,
    dark: darkNamedColors.orange.dark,
  },
};

// Light Theme Variant
// https://planet.invisionapp.com/d/main/#/console/18949169/404930261/preview

const lightNamedColors = getNamedColors(THEME_VARIANTS.light);
const defaultLightPalette = createPalette({mode: THEME_VARIANTS.LIGHT});

export const LIGHT = {
  ...defaultLightPalette,
  ...BASE,
  background: {
    paper: GREY[50],
  },
  divider: GREY[300],
  primary: {
    lighter: TEAL[50],
    light: TEAL[100],
    main: TEAL[500],
    dark: TEAL[800],
    contrastText: GREY[50],
  },
  text: {
    primary: LIGHT_PRIMARY_TEXT,
    secondary: GREY[600],
  },
  mode: THEME_VARIANTS.LIGHT,
  ...lightNamedColors,
  success: {
    light: lightNamedColors.green.light,
    main: lightNamedColors.green.main,
    dark: lightNamedColors.green.dark,
  },
  error: {
    light: lightNamedColors.red.light,
    main: lightNamedColors.red.main,
    dark: lightNamedColors.red.dark,
  },
  info: {
    light: lightNamedColors.blue.light,
    main: lightNamedColors.blue.main,
    dark: lightNamedColors.blue.dark,
  },
  warning: {
    light: lightNamedColors.orange.light,
    main: lightNamedColors.orange.main,
    dark: lightNamedColors.orange.dark,
  },
};

export default {LIGHT, DARK, BASE};
